import * as Sentry from "@sentry/vue"
import * as pkg from "~/package.json"

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  // Plugins run in filename order, so `posthog.client.ts` is already applied here. The plugin
  // `nuxtApp` argument is the untyped `_NuxtApp`; `useNuxtApp()` carries the `$posthog` type.
  const { $posthog } = useNuxtApp()

  if (!config.public.sentry.dsn) {
    return
  }

  Sentry.init({
    app: nuxtApp.vueApp,
    dsn: config.public.sentry.dsn,
    release: `zefire-front@${pkg.version}`,
    environment: config.public.sentry.env,
    ignoreErrors: [
      /Google Maps JavaScript API: Unable to fetch configuration for mapId/,
      /carte est initialisée sans ID de carte valide/,
      // vue3-google-map 0.27.1 unmount race: when the user navigates away while
      // the SDK is still loading, the library's `.then(initMap)` callback fires
      // after the map div has been torn down. Upstream has no disposed-flag guard.
      // Sentry: ZEFIRE-FRONT-1XB.
      /Expected mapDiv of type HTMLElement but was passed null/,
      // Expected after each Vercel deploy: the previous build's manifest is gone. Nuxt handles it
      // (outdated-build check → reload on next navigation) but logs a console.error first.
      // Sentry: ZEFIRE-FRONT-2HP.
      /\/_nuxt\/builds\/meta\/[\w-]+\.json": 404/,
    ],
    integrations: [
      Sentry.captureConsoleIntegration(),
      Sentry.browserTracingIntegration({ router: useRouter() }),
      Sentry.vueIntegration({
        tracingOptions: {
          trackComponents: true,
          hooks: ["activate", "create", "unmount", "mount", "update"],
          timeout: 5000, // milliseconds
        },
      }),
      Sentry.replayIntegration({
        stickySession: true,
        networkDetailAllowUrls: [
          window.location.origin,
          ...config.public.sentry.networkDetailAllowUrls,
        ],
        // https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/configuration/#privacy-configuration
        // By default, don't mask replay data.
        // Only elements with ['.sentry-mask', '[data-sentry-mask]'] will be mased.
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
        //
        networkRequestHeaders: ["Cache-Control"],
        networkResponseHeaders: [
          "Referrer-Policy",
          "x-process-time",
          "x-request-id",
          "x-zefire-version",
        ],
      }),
      Sentry.browserProfilingIntegration(),
      // Tag Sentry events with the PostHog person/session URLs so an issue can be traced back to
      // the product analytics context. Exceptions themselves stay in Sentry only.
      ...($posthog ? [$posthog.sentryIntegration({ sendExceptionsToPostHog: false })] : []),
    ],
    tracePropagationTargets: config.public.sentry.tracePropagationTargets as string[],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    tracesSampleRate: config.public.sentry.tracesSampleRate,
    profilesSampleRate: 1.0, // is 100% of tracesSampleRate

    // Capture Replay for 10% of all sessions, plus for 100% of sessions with an error
    replaysSessionSampleRate: config.public.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.public.sentry.replaysOnErrorSampleRate,

    trackComponents: true,
    logErrors: true, // still log errors in console
  })
})
